import { useEffect, useState } from 'react';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { Container } from '../ui/Container';
import { useLeadModal } from '../LeadModalContext';
import { track } from '../../lib/tracking';
import { cn } from '../../lib/cn';
import { ThuxLogo } from '../brand/ThuxLogo';
import { useT } from '../../i18n/LanguageContext';
import { LanguageSwitcher } from '../../i18n/LanguageSwitcher';
import { AnimatePresence, motion } from 'framer-motion';

export function Navbar() {
  const { open } = useLeadModal();
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const navLinks = [
    { href: '#arquitetura', label: t.nav.howItWorks },
    { href: '#produto', label: t.nav.product },
    { href: '#calculadora', label: t.nav.calculator },
    { href: '#diagnostico', label: t.nav.howItStarts },
  ];

  const handleNavClick = () => setMobileOpen(false);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-40 transition-all duration-300',
        scrolled ? 'pt-3' : 'pt-5'
      )}
    >
      <Container>
        <nav
          aria-label="Navegação principal"
          className={cn(
            'mx-auto flex h-[52px] items-center justify-between',
            'rounded-pill border border-white/10 bg-black/60 backdrop-blur-xl',
            'px-3 pl-5 transition-all duration-300',
            scrolled && 'shadow-card border-white/15'
          )}
        >
          <a
            href="#top"
            className="flex items-center gap-3 text-white"
            aria-label={`Thux · ${t.nav.tagline}`}
          >
            <ThuxLogo variant="light" className="h-7 md:h-8 w-auto" />
            <span
              className="hidden sm:inline-block h-4 w-px bg-white/15"
              aria-hidden
            />
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] text-brand-300/80">
              {t.nav.tagline}
            </span>
          </a>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-3 py-1.5 text-sm text-ink-400 hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <LanguageSwitcher className="hidden sm:block" />

            {/* Desktop CTA */}
            <button
              type="button"
              onClick={() => {
                track('cta_diagnostico_click', { source_section: 'navbar' });
                open('navbar');
              }}
              className={cn(
                'hidden md:inline-flex items-center gap-1.5',
                'h-9 px-4 rounded-pill',
                'bg-bg-pill-secondary border border-white/10',
                'text-sm font-medium text-white',
                'bg-[image:var(--gradient-pill-secondary,radial-gradient(16.7%_42.2%_at_89%_0%,#2E559E_0%,transparent_100%))]',
                'hover:border-brand-300/40 transition-all'
              )}
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
              <span>{t.nav.cta}</span>
            </button>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden h-9 w-9 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-all"
              aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0, 1] }}
              className={cn(
                'md:hidden mt-2 rounded-2xl overflow-hidden',
                'border border-white/10 bg-black/80 backdrop-blur-2xl',
                'shadow-card'
              )}
            >
              <div className="flex flex-col p-4 gap-1">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={handleNavClick}
                    className="flex items-center px-4 py-3 rounded-xl text-base text-ink-300 hover:text-white hover:bg-white/5 transition-all"
                  >
                    {link.label}
                  </a>
                ))}

                <div className="my-2 h-px bg-white/10" />

                <div className="flex items-center justify-between px-4 py-2">
                  <LanguageSwitcher className="sm:hidden" />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    track('cta_diagnostico_click', {
                      source_section: 'navbar_mobile',
                    });
                    open('navbar');
                    setMobileOpen(false);
                  }}
                  className={cn(
                    'mt-1 w-full inline-flex items-center justify-center gap-2',
                    'h-12 rounded-xl',
                    'bg-cta-primary text-white font-heading font-semibold text-base',
                    'border border-brand-300/40 shadow-cta',
                    'hover:shadow-cta-hover transition-all'
                  )}
                >
                  <ArrowUpRight className="h-4 w-4" />
                  <span>{t.nav.cta}</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </header>
  );
}
